import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';

import { ApiModel } from 'shared/models';

import { AutoRestockBrand } from '../../../smart-replenishment/retailer-platform/smart-replenishment/auto-restock/types';
import { SuggestionBrand } from '../../../smart-replenishment/retailer-platform/smart-replenishment/suggestions/types';

@Injectable({
    providedIn: 'root'
})
export class BrandsService {
    private _brandsWithAutoRestockConnection = new ReplaySubject<AutoRestockBrand[]>();
    brandsWithAutoRestockConnection = this._brandsWithAutoRestockConnection.asObservable().pipe(shareReplay(1));
    private _brandsWithSuggestionConnection = new ReplaySubject<SuggestionBrand[]>();
    brandsWithSuggestionConnection = this._brandsWithSuggestionConnection.asObservable().pipe(shareReplay(1));

    private _brandsWithSuggestionsAndAutoRestockConnectionFetched = new BehaviorSubject<boolean>(false);

    constructor(private apiModel: ApiModel) {}

    fetchBrandsWithAutoRestockConnection(): Observable<AutoRestockBrand[]> {
        return this.apiModel
            .makeHttpObservableRequest<AutoRestockBrand[]>({
                method: 'GET',
                url: `${this.apiModel.getAutoRestockEndpoint()}/brands`
            })
            .pipe(
                catchError(() => of([])),
                tap((results) => this._brandsWithAutoRestockConnection.next(results))
            );
    }

    fetchBrandsWithSuggestionConnection(): Observable<SuggestionBrand[]> {
        return this.apiModel
            .makeHttpObservableRequest<SuggestionBrand[]>({
                method: 'GET',
                url: `${this.apiModel.getSuggestionEndpoint()}/brands`
            })
            .pipe(
                catchError(() => of([])),
                tap((results) => this._brandsWithSuggestionConnection.next(results))
            );
    }

    fetchBrandsWithSuggestionsAndAutoRestockConnection(): Observable<[AutoRestockBrand[], SuggestionBrand[]]> {
        return this._brandsWithSuggestionsAndAutoRestockConnectionFetched.pipe(
            switchMap((brandsWithSuggestionsAndAutoRestockConnectionFetched) => {
                if (!brandsWithSuggestionsAndAutoRestockConnectionFetched) {
                    return forkJoin([
                        this.fetchBrandsWithAutoRestockConnection(),
                        this.fetchBrandsWithSuggestionConnection()
                    ]).pipe(tap(() => this._brandsWithSuggestionsAndAutoRestockConnectionFetched.next(true)));
                }
                return combineLatest([this.brandsWithAutoRestockConnection, this.brandsWithSuggestionConnection]);
            })
        );
    }
}
